/*
These actions handle the chain of events when a
user clickes the update button in the overview panel.
*/
import moment from "moment";
import { httpError } from "./error.js";

import fetchretry from "../helpers/retry.js";

const originalFetch = require("isomorphic-fetch");
const fetch = fetchretry(originalFetch);

export const GRANULARITY_MAP = {
  AUTO_100: {
    label: "Auto < 100 bars",
    order: 0
  },
  AUTO_400: {
    label: "Auto < 400 bars",
    order: 1
  },
  HOUR_1: {
    label: "One hour",
    order: 2
  },
  HOUR_3: {
    label: "3 hours",
    order: 3
  },
  HOUR_6: {
    label: "6 hours",
    order: 4
  },
  HOUR_12: {
    label: "12 hours",
    order: 5
  },
  DAY_1: {
    label: "One day",
    order: 6
  },
  WEEK_1: {
    label: "One week",
    order: 7
  },
  MONTH_1: {
    label: "One month",
    order: 8
  }
};

export const PERIOD_MAP = {
  TODAY: {
    label: "Today",
    order: 0,
    getDates: () => ({
      start: moment.utc().startOf("day"),
      end: moment.utc().startOf("day")
    })
  },
  THIS_WEEK: {
    label: "This week",
    order: 1,
    getDates: () => ({
      start: moment.utc().startOf("week"),
      end: moment.utc().startOf("day")
    })
  },
  THIS_MONTH: {
    label: "This month",
    order: 2,
    getDates: () => ({
      start: moment.utc().startOf("month"),
      end: moment.utc().startOf("day")
    })
  },
  ONE_MONTH: {
    label: "One month",
    order: 3,
    getDates: () => ({
      start: moment.utc().subtract(1, "months"),
      end: moment.utc().startOf("day")
    })
  },
  LAST_MONTH: {
    label: "Last month",
    order: 4,
    getDates: () => ({
      start: moment.utc().startOf("month").subtract(1, "months"),
      end: moment.utc().startOf("month").subtract(1, "day")
    })
  },
  THREE_MONTHS: {
    label: "Three months",
    order: 5,
    getDates: () => ({
      start: moment.utc().subtract(3, "months"),
      end: moment.utc().startOf("day")
    })
  },
  THIS_YEAR: {
    label: "This year",
    order: 6,
    getDates: () => ({
      start: moment.utc().startOf("year"),
      end: moment.utc().startOf("day")
    })
  },
  LAST_TWO_YEARS: {
    label: "Two years",
    order: 7,
    getDates: () => ({
      start: moment.utc().subtract(2, "years"),
      end: moment.utc().startOf("day")
    })
  },
  DIVIDER: { label: "DIVIDER", order: 7 },
  CUSTOM: { label: "Custom", order: 8 }
};

export const REQUEST_OV_TIME_SERIES = "REQUEST_OV_TIME_SERIES";
export const RECEIVE_OV_TIME_SERIES = "RECEIVE_OV_TIME_SERIES";
export const REQUEST_OV_ACCOUNT_SERIES = "REQUEST_OV_ACCOUNT_SERIES";
export const RECEIVE_OV_ACCOUNT_SERIES = "RECEIVE_OV_ACCOUNT_SERIES";

export const requestTimeSeries = () => ({
  type: REQUEST_OV_TIME_SERIES
});

export const receiveTimeSeries = json => ({
  type: RECEIVE_OV_TIME_SERIES,
  data: json.data
});

export const requestAccountSeries = () => ({
  type: REQUEST_OV_ACCOUNT_SERIES
});

export const receiveAccountSeries = json => ({
  type: RECEIVE_OV_ACCOUNT_SERIES,
  data: json.data
});


const buildStorageUsageUrl = (start, end, accountId = null) => {
  let url = `/storage?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
  if (accountId) {
    url += `&account_id=${encodeURIComponent(accountId)}`;
  }
  return url;
};


const dateRangeFilters = period => {
  const { start, end } = period;
  const endPlusOne = moment.utc(end).add(1, "day");
  const filters = [];
  filters.push(
    `{"field": "start_time", "op": ">=", "value": "${start.format(
      "YYYY-MM-DD"
    )}"}`
  );
  filters.push(
    `{"field": "start_time", "op": "<",  "value": "${endPlusOne.format(
      "YYYY-MM-DD"
    )}"}`
  );
  return filters;
};

const equalityFilter = (field, value) => {
  return `{"field": "${field}", "op": "=",  "value": "${value}"}`;
};


const fetchStorageUsage = async (account, period, webtoken) => {
 
  const start_str = period.start.format("YYYY-MM-DD");
  const end_str = period.end.format("YYYY-MM-DD");
  const accountId = account.selected ? account.selected.id : null;

  const url = buildStorageUsageUrl(start_str, end_str, accountId);
 

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${webtoken}`
      }
    });

    if (!response.ok) {
      throw Error(response.statusText);
    }

    const json = await response.json();
    const filteredData = json.data.filter(entry => parseFloat(entry.cost) > 0);
 
    return filteredData;
  } catch (error) {
    console.log("fetchStorageUsageHelper error", error);
    throw error;
  }
}


export function fetchTimeSeries() {
  return async function (dispatch, getState) {
    dispatch(requestTimeSeries());

    const state = getState();
    const { webtoken } = state.prerequisites;

    const urlPrefix = `/usage`;

    const { account, period } = state.overview.filters;

    const accountId = account.selected.id;

    const filters = dateRangeFilters(period);

    if (accountId) {
      filters.push(equalityFilter("account_id", accountId));
    }

    const grpStr = encodeURIComponent(`["start_time"]`);
    const filterStr = encodeURIComponent(`[${filters.join(",")}]`);
    const url = `${urlPrefix}?filter=${filterStr}&group_by=${grpStr}`;
    
    try {
      const usageResponse = await fetch(url, {
        headers: {
          Authorization: `Bearer ${webtoken}`
        }
      });
      if (!usageResponse.ok) {
        throw Error(usageResponse.statusText);
      }
      const usageJson = await usageResponse.json();
      const storageJson = await fetchStorageUsage(account, period, webtoken);
      const combinedData = combineTimeSeries(usageJson, storageJson);

      dispatch(receiveTimeSeries(combinedData));
    } catch (error) {
      dispatch(httpError(error));
    }

  };
}


export function fetchAccountSeries() {
  return async function (dispatch, getState) {
    dispatch(requestAccountSeries());

    const state = getState();
    const { webtoken } = state.prerequisites;

    const urlPrefix = `/usage`;


    const { account, period } = state.overview.filters;
 

    const filters = dateRangeFilters(period);

    const filterStr = encodeURIComponent(`[${filters.join(",")}]`);
    const grpStr = encodeURIComponent(`["start_time", "account_id"]`);
    const projStr = encodeURIComponent(`["sum(cores)", "sum(instance_cost)", "sum(license_cost)", "sum(minutes)", "start_time", "account_id"]`);
    const url = `${urlPrefix}?filter=${filterStr}&group_by=${grpStr}&projection=${projStr}`;

    try {
      const usageResponse = await fetch(url, {
        headers: {
          Authorization: `Bearer ${webtoken}`
        }
      });
      if (!usageResponse.ok) {
        throw Error(usageResponse.statusText);
      }
      const usageJson = await usageResponse.json();
      const storageJson = await fetchStorageUsage(account, period, webtoken);
      const combinedData = combineAccountSeries(usageJson, storageJson);

      dispatch(receiveAccountSeries(combinedData));
    } catch (error) {
      dispatch(httpError(error));
    }
  };
}

function addDays(date, days) {
  const copy = new Date(Number(date));
  copy.setDate(copy.getDate() + days);
  return copy;
}

function formatStartDate(date) {
  date.setUTCHours(23, 59, 59, 0);
  return date.toUTCString();
}


function combineAccountSeries(usageJson, storageJson) {

  usageJson.data.forEach(entry => {
    entry.storage = 0;
    entry.storage_cost = 0;
  });
 
  storageJson.forEach(storage => {
    const startDate = new Date(storage.start_date);
    const costPerDay = storage.cost_per_day;
    const gibsPerDay = storage.gibs_per_day;

    for (let i = 0; i < costPerDay.length; i++) {
      const currentDate = addDays(startDate, i);

      usageJson.data.push({
        "account_id": storage.account_id,
        "cores": 0,
        "instance_cost": 0,
        "license_cost": 0,
        "minutes": 0,
        "self_link": 0,
        "start_time": formatStartDate(currentDate),
        "storage": parseFloat(gibsPerDay[i]),
        "storage_cost": parseFloat(costPerDay[i])
      });
    }
  });


  return usageJson;
}

function combineTimeSeries(usageJson, storageJson) {

  usageJson.data.forEach(entry => {
    entry.storage = 0;
    entry.storage_cost = 0;
  });

  let combinedUsage = {};

  storageJson.forEach(storage => {
    const startDate = new Date(storage.start_date);
    const costPerDay = storage.cost_per_day;
    const gibsPerDay = storage.gibs_per_day;

    for (let i = 0; i < costPerDay.length; i++) {
      const currentDate = formatStartDate(addDays(startDate, i));

      if (!combinedUsage[currentDate]) {
        combinedUsage[currentDate] = {
          "cores": 0,
          "instance_cost": 0,
          "license_cost": 0,
          "minutes": 0,
          "self_link": 0,
          "start_time": currentDate,
          "storage": 0,
          "storage_cost": 0
        };
      }

      combinedUsage[currentDate].storage += parseFloat(gibsPerDay[i]);
      combinedUsage[currentDate].storage_cost += parseFloat(costPerDay[i]);
    }
  });

  Object.values(combinedUsage).forEach(dailyUsage => {
    usageJson.data.push(dailyUsage);
  });

  return usageJson;
}




// WEBPACK FOOTER //
// ./src/actions/usage.js